import { Article, Delete, Description, GetApp, GridOn, PictureAsPdf, Upload } from '@mui/icons-material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { useResellerLimitDialog } from 'src/components/reseller-limit-context/resellerLimitContext';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const AiCommentMessageComponent = () => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const isCustomDomain = CommonHelper.CustomDomain();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage("is_portal_admin");
  const [suggestion, setSuggestion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [fetchedMedia, setFetchedMedia] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteThreadDialog, setOpenDeleteThreadDialog] = useState(false); // New state for thread deletion dialog
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [downloading, setDownloading] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const { showResellerLimitDialog } = useResellerLimitDialog();
  const fileInputRef = useRef(null);
  const blobUrlsRef = useRef(new Set());

  useEffect(() => {
    fetchAiSuggestion();
  }, []);

  useEffect(() => {
    if (suggestion?.thread_id && suggestion?.assistant_id) {
      fetchMediaDetails();
    }
  }, [suggestion]);

  const fetchAiSuggestion = () => {
    setIsLoading(true);
    let getProps = {
      url: userModuleURL.getAiSuggestion,
      successCallback: (data, message) => {
        setSuggestion(data || null);
        setIsLoading(false);
      },
      failureCallback: (message) => {
        setIsLoading(false);
        showToast(message || 'Failed to fetch AI suggestion', 'e');
      },
    };
    HttpServices.Get(getProps);
  };

  const fetchMediaDetails = async () => {
    setIsLoading(true);
    try {
      await HttpServices.Get({
        url: userModuleURL.fetchBrandMedia,
        successCallback: async (data, message) => {
          if (data && Array.isArray(data)) {
            blobUrlsRef.current.forEach((url) => {
              URL.revokeObjectURL(url);
            });
            blobUrlsRef.current.clear();

            const mediaPromises = data.map(async (media) => {
              try {
                const mediaUrl = await HttpServices.GetImageFromAPI(media.media_url);
                if (mediaUrl) {
                  blobUrlsRef.current.add(mediaUrl);
                  return {
                    url: mediaUrl,
                    original_url: media.media_url,
                    display_name: media.display_name,
                    file_type: media.file_type.toLowerCase(),
                    isLoading: false,
                  };
                }
                return null;
              } catch (error) {
                showToast(`Failed to fetch media: ${media.display_name}`, 'e');
                return null;
              }
            });
            const resolvedMedia = (await Promise.all(mediaPromises)).filter(Boolean);
            setFetchedMedia(resolvedMedia);
            setIsLoading(false);
            message && showToast(message, 's');
          } else {
            setFetchedMedia([]);
            setIsLoading(false);
            showToast('No media found', 'e');
          }
        },
        failureCallback: (message) => {
          setIsLoading(false);
          showToast(message || 'Failed to fetch media details', 'e');
        },
      });
    } catch (error) {
      setIsLoading(false);
      showToast('Error fetching media details', 'e');
    }
  };

  const handleDownload = async (url, displayName, fileType) => {
    if (downloading === displayName) return;
    setDownloading(displayName);

    try {
      const blobUrl = await HttpServices.GetImageFromAPI(url);
      if (blobUrl) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = displayName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast(translate('ai_message_comments.downloaded', 'Downloaded {{displayName}}', { displayName }), 's');
      } else {
        throw new Error('Failed to fetch file');
      }
    } catch (error) {
      showToast(
        translate('ai_message_comments.failed_to_download', 'Failed to download {{displayName}}', { displayName }),
        'e'
      );
    } finally {
      setDownloading(null);
    }
  };

  const createAssistant = () => {
    setIsLoading(true);
    let postProps = {
      url: userModuleURL.createAiAssistant,
      successCallback: (data, message) => {
        fetchAiSuggestion();
        showToast(translate('ai_message_comments.assistant_created', 'Assistant created successfully'), 's');
      },
      failureCallback: (message) => {
        setIsLoading(false);
        if (message && !showResellerLimitDialog(message)) {
          showToast(message, 'e');
        }
      },
    };
    HttpServices.Post(postProps);
  };

  const handleFileChange = (e) => {
    const selectedFiles = e.target.files || e.dataTransfer.files;
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const validTypes = [
        'application/pdf',
        'text/plain',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
      ];
      if (!validTypes.includes(file.type)) {
        showToast(
          translate(
            'ai_message_comments.invalid_file_type',
            'Invalid file type: {{fileName}}. Only PDF, TXT, DOC, and DOCX files are allowed.',
            { fileName: file.name }
          ),
          'e'
        );
        return false;
      }
      return true;
    });

    setFiles((prevFiles) => {
      const existingFileNames = new Set(prevFiles.map((file) => file.name));
      const newFiles = validFiles.filter((file) => !existingFileNames.has(file.name));
      return [...prevFiles, ...newFiles];
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e);
    }
  };

  const saveSuggestion = () => {
    if (files.length === 0) {
      showToast(translate('ai_message_comments.please_select_valid_files', 'Please select valid files'), 'e');
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append('file', file));
    HttpServices.SaveAiContent({
      formData,
      setFileUploadProgress,
      successCallback: async (data, message) => {
        setIsLoading(false);
        fetchAiSuggestion();
        showToast(message, 's');
      },
      failureCallback: (message) => {
        setIsLoading(false);
        if (message && !showResellerLimitDialog(message)) {
          showToast(message, 'e');
        }
      },
    });
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    setIsLoading(true);
    let deleteProps = {
      url: userModuleURL.deleteAiContent,
      successCallback: (data, message) => {
        setSuggestion(null);
        blobUrlsRef.current.forEach((url) => {
          URL.revokeObjectURL(url);
        });
        blobUrlsRef.current.clear();
        setFetchedMedia([]);
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setIsLoading(false);
        setOpenDeleteDialog(false);
        showToast(
          message || translate('ai_message_comments.ai_suggestion_deleted', 'AI suggestion deleted successfully'),
          's'
        );
      },
      failureCallback: (message) => {
        setIsLoading(false);
        showToast(
          message || translate('ai_message_comments.failed_to_delete_ai_suggestion', 'Failed to delete AI suggestion'),
          'e'
        );
      },
    };
    HttpServices.Post(deleteProps);
  };

  const handleDeleteThread = () => {
    setOpenDeleteThreadDialog(true);
  };

  const confirmDeleteThread = () => {
    setIsLoading(true);
    let deleteProps = {
      url: userModuleURL.deleteAiThread,
      successCallback: (data, message) => {
        setSuggestion({ ...suggestion, thread_id: '' });
        blobUrlsRef.current.forEach((url) => {
          URL.revokeObjectURL(url);
        });
        blobUrlsRef.current.clear();
        setFetchedMedia([]);
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setIsLoading(false);
        setOpenDeleteThreadDialog(false);
        fetchAiSuggestion();
        showToast(message || translate('ai_message_comments.ai_thread_deleted', 'AI thread deleted successfully'), 's');
      },
      failureCallback: (message) => {
        setIsLoading(false);
        showToast(
          message || translate('ai_message_comments.failed_to_delete_ai_thread', 'Failed to delete AI thread'),
          'e'
        );
      },
    };
    HttpServices.Post(deleteProps);
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'application/pdf':
      case 'pdf':
        return <PictureAsPdf color="primary" />;
      case 'text/plain':
      case 'txt':
        return <Description color="primary" />;
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'application/msword':
      case 'doc':
      case 'docx':
        return <Article color="primary" />;
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      case 'application/vnd.ms-excel':
      case 'xls':
      case 'xlsx':
      case 'excel':
        return <GridOn color="primary" />;
      default:
        return <Description color="primary" />;
    }
  };

  const renderFilePreview = (item, index, isUploaded = false) => {
    const fileType = isUploaded ? item.file_type : item.type || getFileTypeFromUrl(item.url);
    const isPdf = fileType === 'application/pdf' || fileType === 'pdf';

    return (
      <ListItem
        key={index}
        sx={{
          bgcolor: 'background.paper',
          mb: 1,
          borderRadius: 1,
          flexDirection: 'column',
          alignItems: 'flex-start',
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <ListItemIcon>{getFileIcon(fileType)}</ListItemIcon>
          <ListItemText primary={isUploaded ? item.display_name : item.name} secondary={fileType} />
          {!isUploaded && (
            <IconButton onClick={() => removeFile(index)} color="error">
              <Delete />
            </IconButton>
          )}
        </Box>
        {isUploaded && isPdf && (
          <Box
            sx={{
              mt: 1,
              width: '100%',
              minHeight: 500,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'auto',
              position: 'relative',
              bgcolor: 'white',
            }}
          >
            {item.isLoading ? (
              <CircularProgress
                sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              />
            ) : (
              <iframe
                src={item.url}
                title={item.display_name}
                width="100%"
                height="100%"
                style={{ border: 'none', minHeight: 500 }}
                scrolling="auto"
              >
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  Unable to preview PDF. Your browser may not support PDF rendering.
                </Typography>
              </iframe>
            )}
          </Box>
        )}
        {isUploaded && !isPdf && (
          <Box
            sx={{
              mt: 1,
              width: '100%',
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {translate('common.preview_not_available', 'Preview not available for')} {fileType}.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetApp />}
              onClick={() => handleDownload(item.original_url, item.display_name, fileType)}
              disabled={downloading === item.display_name || isLoading}
              sx={{ mt: 1, mr: 2 }}
            >
              {downloading === item.display_name
                ? translate('common.downloading', 'Downloading...')
                : `${translate('common.download', 'Download')} ${item.display_name}`}
            </Button>
          </Box>
        )}
      </ListItem>
    );
  };

  const getFileTypeFromUrl = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'txt':
        return 'text/plain';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto', bgcolor: 'background.default', borderRadius: 2, boxShadow: 3 }}>
      <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
        {translate('ai_message_comments.manager_title', 'AI Comment Message Manager')}
      </Typography>
      {(!isCustomDomain || isPortalAdmin) && (
        <Button
          variant="outlined"
          startIcon={<YouTubeIcon />}
          onClick={() => window.open('https://www.youtube.com/watch?v=kmHBX6h18UU', '_blank')}
          color="primary"
        >
          {translate('common.tutorial')}
        </Button>
      )}
      {isLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}
      {!isLoading && suggestion?.assistant_id === '' && (
        <Card sx={{ mt: 2, p: 2, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              {translate(
                'ai_message_comments.no_ai_suggestion',
                'No AI suggestion available. Create an assistant to get started.'
              )}
            </Typography>
            <Button variant="contained" color="primary" onClick={createAssistant} disabled={isLoading} sx={{ mt: 2 }}>
              {translate('ai_message_comments.create_assistant', 'Create Assistant')}
            </Button>
          </CardContent>
        </Card>
      )}
      {!isLoading && suggestion?.assistant_id !== '' && suggestion?.thread_id === '' && (
        <Card sx={{ mt: 2, p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="text.primary" sx={{ fontWeight: 'medium' }}>
              {translate('ai_message_comments.upload_media', 'Upload Media')}
            </Typography>
            <Paper
              variant="outlined"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                p: 3,
                mb: 2,
                border: `2px dashed ${dragActive ? '#1976d2' : '#bdbdbd'}`,
                borderRadius: 2,
                bgcolor: dragActive ? 'primary.light' : 'grey.50',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <input
                type="file"
                multiple
                accept=".pdf,.txt,.doc,.docx,.xls,.xlsx"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button variant="contained" color="primary" startIcon={<Upload />} component="span" sx={{ mb: 1 }}>
                  Select Files
                </Button>
              </label>
              <Typography variant="body2" color="text.secondary">
                {translate(
                  'ai_message_comments.drag_drop_hint',
                  'Drag and drop files here or click to select (PDF, TXT, DOC, DOCX, XLS, XLSX)'
                )}
              </Typography>
            </Paper>
            {files.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  {translate('ai_message_comments.selected_files', 'Selected Files:')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                    p: 1,
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                  }}
                >
                  {files.map((file, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        transition: 'box-shadow 0.3s ease',
                        '&:hover': { boxShadow: 3 },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ListItemIcon>{getFileIcon(file.type)}</ListItemIcon>
                          <Box>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {file.type}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton onClick={() => removeFile(index)} color="error" size="small">
                          <Delete />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
            {fileUploadProgress > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {translate('ai_message_comments.upload_progress', 'Upload Progress: {{progress}}%', {
                    progress: fileUploadProgress,
                  })}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={fileUploadProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'grey.300',
                    '& .MuiLinearProgress-bar': { bgcolor: 'success.main' },
                  }}
                />
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={saveSuggestion}
              disabled={isLoading || files.length === 0}
              sx={{ mt: 2, px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              {translate('ai_message_comments.save_ai_suggestion', 'Save AI Suggestion')}
            </Button>
          </CardContent>
        </Card>
      )}
      {!isLoading && suggestion?.thread_id && suggestion?.assistant_id && (
        <Card sx={{ mt: 2, p: 2, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translate('ai_message_comments.ai_suggestion_details', 'AI Suggestion Details')}
            </Typography>
            <Typography>
              <strong>{translate('ai_message_comments.assistant_id', 'Assistant ID:')}</strong>{' '}
              {suggestion.assistant_id}
            </Typography>
            <Typography>
              <strong>{translate('ai_message_comments.thread_id', 'Thread ID:')}</strong> {suggestion.thread_id}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              {translate('ai_message_comments.media_preview', 'Media Preview:')}
            </Typography>
            <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1 }}>
              {fetchedMedia.map((media, index) => renderFilePreview(media, index, true))}
            </List>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="error" onClick={handleDelete} disabled={isLoading}>
                {translate('ai_message_comments.delete_ai_content', 'Delete AI Content')}
              </Button>
              <Button variant="outlined" color="error" onClick={handleDeleteThread} disabled={isLoading}>
                {translate('ai_message_comments.delete_uploaded_media', 'Delete Uploaded Media')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        title={translate('ai_message_comments.delete_ai_content', 'Delete AI Content')}
        message={translate(
          'ai_message_comments.delete_ai_content_confirm',
          'Are you sure you want to delete all AI-generated content? This action cannot be undone.'
        )}
      />
      <ConfirmationDialog
        open={openDeleteThreadDialog}
        onClose={() => setOpenDeleteThreadDialog(false)}
        onConfirm={confirmDeleteThread}
        title={translate('ai_message_comments.delete_uploaded_media', 'Delete Uploaded Media')}
        message={translate(
          'ai_message_comments.delete_uploaded_media_confirm',
          'Are you sure you want to delete all uploaded media files? This will also reset the current conversation.'
        )}
      />
    </Box>
  );
};

export default AiCommentMessageComponent;
